import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS
const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

/*
 * Indigo, chosen against a constraint rather than a preference.
 *
 * In a revenue panel three hue regions already carry meaning in the data — green is
 * growth, red is loss and refunds, amber is pending — so the accent has to stay clear of
 * all three or it weakens a signal the team reads every day. Indigo sits 128 degrees from
 * success, 128 from error and 171 from warning.
 *
 * `main` gives 6.34:1 on white, which clears the text threshold in both directions at
 * once: white text sits on it as a button fill, and it also works as link text on white.
 * On a dark ground use `light` — `main` drops to 2.70:1 against #161C24 while `light`
 * holds at 5.92:1. Light and dark never share a tone.
 */
const PRIMARY = {
  lighter: '#DBDDF0',
  light: '#7E89E7',
  main: '#3D4EDB',
  dark: '#202EAA',
  darker: '#162076',
  contrastText: '#fff',
};
/*
 * Not a second brand colour — there is only one. This is an accessory tone for the few
 * places MUI needs one (Chip outlines, Fab, the cohort table's highlight). It was
 * #3366FF, which sits 9 degrees from the accent and was effectively the same colour.
 */
const SECONDARY = {
  lighter: '#F0DBE8',
  light: '#E580BF',
  main: '#B0247C',
  dark: '#7F1A59',
  darker: '#58123E',
  contrastText: '#fff',
};
/* Was #1890FF, 25 degrees from the accent. Moved to cyan, 42 degrees clear. */
const INFO = {
  lighter: '#D8EEF3',
  light: '#72DBF3',
  main: '#0B6E85',
  dark: '#084F60',
  darker: '#053742',
  contrastText: '#fff',
};
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
  contrastText: GREY[800],
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: GREY[800],
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#fff',
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
};

/*
 * Series colours. Charts draw these next to `primary.main`, so each family has to be
 * clearly separated from the accent and from the others — two adjacent series a reader
 * cannot tell apart is the one failure a chart palette cannot have.
 *
 * `violet` moved from #826AF9 (17 degrees from the accent) and `blue` became `magenta`
 * because there is no room left for a blue: the gap between `green` — which is really a
 * turquoise at 173 degrees — and indigo at 234 is too narrow to hold one. Every family
 * now sits at least 41 degrees from its nearest neighbour.
 */
const CHART_COLORS = {
  violet: ['#B44BD8', '#CD8DE2', '#E9D0F1', '#F3E8F7'],
  magenta: ['#D8478F', '#E289B5', '#F1CCDE', '#F8E8F0'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

// APPLICATION-SPECIFIC SEMANTIC COLORS
// Exported so non-theme code (store filter dots) can read the same values rather than
// keeping a second copy of them.
export const APP_COLORS = {
  subscription: {
    active: SUCCESS.main,
    inactive: GREY[500],
    trial: WARNING.main,
    expired: ERROR.main,
  },
  transaction: {
    completed: SUCCESS.main,
    pending: WARNING.main,
    failed: ERROR.light,
    refunded: INFO.main,
    // Literals, not aliases of PRIMARY/SECONDARY. These name a kind of money movement,
    // and a brand colour change must never silently repaint the data underneath it.
    purchase: '#3D4EDB',
    renewal: '#0E9F9F',
    cancellation: ERROR.main,
  },
  /*
   * `ps` and `as` used to be PRIMARY and SECONDARY, so changing the brand recoloured
   * Google Play and the App Store without anyone deciding to. They are literals now.
   *
   * These are only ever chips and filter dots — never a chart series — so unlike the
   * status colours they do not have to keep their distance from the accent. Their job is
   * recognition, which is why `as` is Apple's own blue: a neutral grey here reads as
   * "unknown" rather than as a platform, which is exactly how it failed in review.
   */
  platform: {
    ps: '#0E9F9F',
    as: '#007AFF',
    web: '#B0247C',
    unknown: GREY[500_56],
  },
  delivery: {
    success: SUCCESS.main,
    failed: ERROR.main,
    pending: WARNING.main,
  },
  consumption: {
    undeclared: GREY[500],
    notConsumed: WARNING.main,
    partiallyConsumed: INFO.main,
    fullyConsumed: SUCCESS.main,
  },
  refund: {
    pending: WARNING.main,
    approved: SUCCESS.main,
    rejected: ERROR.main,
    noPreference: INFO.main,
    undeclared: GREY[500],
  },
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY },
  secondary: { ...SECONDARY },
  info: { ...INFO },
  success: { ...SUCCESS },
  warning: { ...WARNING },
  error: { ...ERROR },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  app: APP_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    text: { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: '#fff', neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    text: {
      primary: '#fff',
      secondary: alpha('#fff', 0.7),
      disabled: alpha('#fff', 0.5),
    },
    background: {
      paper: '#1E1E1E',
      default: '#121212',
      neutral: alpha('#fff', 0.05),
    },
    action: { active: GREY[500], ...COMMON.action },
    components: {
      cardBackground: '#2D2D2D',
      tableHeader: '#333333',
      tableRow: {
        hover: alpha('#fff', 0.1),
      },
      nav: {
        background: '#1A1A1A',
        hover: alpha('#fff', 0.08),
        selected: alpha('#fff', 0.16),
      },
      border: alpha('#fff', 0.15),
    },
  },
};

export default palette;
